import { useEffect } from "react";
import { MapPin, Clock } from "lucide-react";
import { NEIGHBORHOODS, NEARBY_CITIES, IMAGES } from "@/data/site";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const areaBlurbs = {
  Woodbridge: "Many Woodbridge homes built in the late '70s–'80s still run original-generation water heater closets — we handle tight-space replacements and code updates here weekly.",
  "University Park": "One of Irvine's earliest villages; we frequently upgrade aging tanks to high-efficiency and tankless units in University Park's classic single-stories.",
  Woodbury: "Newer Woodbury homes often have tankless units due for their first descale — we service and maintain Navien and Rinnai systems throughout the village.",
  "Portola Springs": "Fast-growing Portola Springs is tankless country; we handle warranty service, descaling, and capacity upgrades for busy households.",
  "Orchard Hills": "Larger Orchard Hills homes often need 75-gallon or high-capacity tankless systems — proper sizing is everything, and it's what we do.",
  "Turtle Rock": "Established Turtle Rock properties frequently have attic and closet installs where a leak is high-stakes — we prioritize emergency response here.",
  "Quail Hill": "Quail Hill's 2000s-era tanks are hitting end-of-life right now; same-day replacement is our most-requested service in the area.",
  Northwood: "From original Northwood tract homes to newer builds, we repair and replace gas tanks and convert to tankless across the village.",
  Westpark: "Westpark's mid-'90s water heaters are prime candidates for replacement — we include all current code upgrades in our flat price.",
  "Oak Creek": "Garage-install replacements and tankless conversions are the daily bread of our Oak Creek routes.",
  "Cypress Village": "Newer Cypress Village homes get manufacturer-warranty service and annual tankless maintenance from our certified techs.",
  "Great Park": "Great Park neighborhoods run almost entirely on tankless — we're certified on every major brand installed there.",
};

export default function ServiceArea() {
  useEffect(() => {
    document.title = "Service Area — Irvine & Orange County | Irvine Water Heater Man";
  }, []);

  return (
    <div>
      <section className="navy-grain text-white px-4 sm:px-6 pt-14 sm:pt-20 pb-16" data-testid="area-hero">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            <MaskedLine>Water Heater Service</MaskedLine>
            <MaskedLine delay={0.15}><span className="text-[#FF6500]">Across Irvine &amp; OC.</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="text-white/70 text-lg leading-relaxed max-w-2xl mt-6">
              Based at the center of Irvine, our trucks reach every village — and the surrounding Orange County cities — usually within the hour for emergencies.
            </p>
          </MaskedLine>
          <div className="flex items-center gap-2 mt-6 text-white/70">
            <Clock className="w-4 h-4 text-[#FF6500]" /> 24/7 emergency dispatch · 60–90 min target inside Irvine
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white" data-testid="area-neighborhoods">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">Irvine Villages</p>
            <h2 className="font-heading font-extrabold text-4xl tracking-tight text-gray-900 mb-12">Every Village, Covered.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {NEIGHBORHOODS.map((n, i) => (
              <Reveal key={n} delay={(i % 3) * 0.06}>
                <div className="border border-gray-200 rounded-2xl p-7 h-full hover:border-[#FF6500] transition-colors" data-testid={`area-card-${n.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-[#FF6500]" />
                    <h3 className="font-heading font-bold text-xl text-gray-900">{n}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{areaBlurbs[n]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-[#FAFAFA]" data-testid="area-cities">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">Beyond Irvine</p>
            <h2 className="font-heading font-extrabold text-4xl tracking-tight text-gray-900 mb-6">Nearby Orange County Cities.</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              The same water heater specialists, same flat-rate pricing, and same 24/7 availability extend to our neighboring cities:
            </p>
            <div className="flex flex-wrap gap-2">
              {NEARBY_CITIES.map((c) => (
                <span key={c} className="bg-[#0B192C] text-white rounded-full px-5 py-2 text-sm font-heading font-bold">{c}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={IMAGES.van} alt="Service van covering Orange County" className="rounded-2xl w-full h-[380px] object-cover" />
          </Reveal>
        </div>
      </section>

      <CTABand idPrefix="area-cta" />
    </div>
  );
}
